import { useState } from 'react'
import { ChevronLeft, ChevronRight, Download, Printer, Receipt } from 'lucide-react'
import { usePaymentHistory } from '../hooks/queries/usePaymentHistory'
import { ResponsiveTable, type Column } from './ResponsiveTable'
import { Card, Button, Spinner } from './ui'
import ReceiptDialog from './ReceiptDialog'
import type { PaymentHistoryItem } from '../utils/api'

/**
 * お支払い履歴セクション（BillingPage）。
 *
 * ログインユーザー自身の課金記録（processed_payments）を新しい順で表示する。
 * 経費精算・確定申告向けに、ページ送り表示・印刷・CSV出力に対応する。
 * バックエンドの GET /billing/history は本人分を全件返すため、
 * ページ送り／印刷／CSV はいずれも取得済みの全件データからクライアント側で生成する
 * （ユーザー1人あたりの件数は少なく、全件保持のコストは無視できる）。
 */

const PAGE_SIZE = 10

interface PaymentHistoryProps {
  /** 印刷・CSV のヘッダ、領収書の宛名初期値に使うアカウント識別（任意） */
  userEmail?: string
  /** 領収書の宛名初期値に優先して使う表示名（任意） */
  userName?: string
}

/** ISO 日時を日本語の日付表記（YYYY/MM/DD）へ整形する。 */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ja-JP')
}

/** 決済種別を「内容」ラベルへ変換する（種別 + ワンショットの追加時間・追加回数）。 */
function describePayment(item: PaymentHistoryItem): string {
  if (item.payment_type === 'oneshot') {
    if ((item.sessions ?? 0) > 0) return `追加回数（+${item.sessions}回）`
    return item.hours > 0 ? `追加時間（+${item.hours}時間）` : '追加時間'
  }
  if (item.payment_type === 'subscription') {
    return 'プラン（月額）'
  }
  if (item.payment_type === 'plan_change') {
    return 'プラン変更（日割り差額）'
  }
  return item.payment_type
}

/** 金額（円）の表示文字列。未記録・0円は '—'。 */
function formatAmount(amount: number | null): string {
  return amount != null && amount > 0 ? `¥${amount.toLocaleString()}` : '—'
}

const columns: Column<PaymentHistoryItem>[] = [
  { key: 'date', header: '日付', render: (row) => formatDate(row.created_at) },
  { key: 'content', header: '内容', render: (row) => describePayment(row) },
  { key: 'amount', header: '金額', align: 'right', render: (row) => formatAmount(row.amount) },
]

/** 全件を CSV 文字列へ変換する（Excel 互換のため BOM 付与、CRLF 改行）。 */
function toCsv(rows: PaymentHistoryItem[]): string {
  const escapeCell = (v: string) => `"${v.replace(/"/g, '""')}"`
  const header = ['日付', '内容', '金額(円)']
  const body = rows.map((r) => [
    formatDate(r.created_at),
    describePayment(r),
    r.amount != null && r.amount > 0 ? String(r.amount) : '',
  ])
  const csv = [header, ...body].map((cols) => cols.map(escapeCell).join(',')).join('\r\n')
  return '﻿' + csv
}

/** ブラウザ上で CSV をダウンロードさせる。 */
function downloadCsv(rows: PaymentHistoryItem[]): void {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
  const a = document.createElement('a')
  a.href = url
  a.download = `otomochi_payment_history_${stamp}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** HTML 特殊文字をエスケープする（印刷ドキュメント埋め込み用）。 */
function escapeHtml(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** 別ウィンドウに印刷用の明細を描画し、印刷ダイアログを開く。 */
function printHistory(rows: PaymentHistoryItem[], userEmail?: string): void {
  const win = window.open('', '_blank', 'width=820,height=640')
  if (!win) return // ポップアップブロック時は無操作

  const bodyRows = rows
    .map(
      (r) => `<tr>
        <td>${escapeHtml(formatDate(r.created_at))}</td>
        <td>${escapeHtml(describePayment(r))}</td>
        <td class="amount">${escapeHtml(formatAmount(r.amount))}</td>
      </tr>`,
    )
    .join('')

  win.document.write(`<!doctype html>
<html lang="ja"><head><meta charset="utf-8" />
<title>otomochi お支払い履歴</title>
<style>
  body { font-family: sans-serif; color: #1f2937; margin: 32px; }
  h1 { font-size: 20px; margin: 0 0 4px; }
  .meta { font-size: 13px; color: #6b7280; margin: 0 0 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
  th { color: #6b7280; font-weight: 600; }
  td.amount, th.amount { text-align: right; }
  @media print { body { margin: 0; } }
</style></head>
<body>
  <h1>otomochi お支払い履歴</h1>
  <p class="meta">
    ${userEmail ? `アカウント: ${escapeHtml(userEmail)}<br />` : ''}
    発行日: ${escapeHtml(formatDate(new Date().toISOString()))}
  </p>
  <table>
    <thead><tr><th>日付</th><th>内容</th><th class="amount">金額</th></tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body></html>`)
  win.document.close()
  win.focus()
  win.print()
}

export default function PaymentHistory({ userEmail, userName }: PaymentHistoryProps) {
  const { data, isLoading, isError } = usePaymentHistory()
  const [page, setPage] = useState(1)
  const [receiptItem, setReceiptItem] = useState<PaymentHistoryItem | null>(null)

  const rows = data ?? []
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  // データ更新でページ数が減った場合に範囲外にならないよう丸める
  const currentPage = Math.min(page, totalPages)
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const hasRows = rows.length > 0

  return (
    <Card className="mt-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold">お支払い履歴</h2>

        {hasRows && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => printHistory(rows, userEmail)}
              className="gap-1"
            >
              <Printer className="w-4 h-4" />
              印刷
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => downloadCsv(rows)}
              className="gap-1"
            >
              <Download className="w-4 h-4" />
              CSV
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      ) : isError ? (
        <p className="py-8 text-center text-text-sub">お支払い履歴の取得に失敗しました</p>
      ) : (
        <>
          <ResponsiveTable
            columns={columns}
            rows={pageRows}
            rowKey={(row) => `${row.created_at}-${row.payment_type}-${row.amount ?? 0}`}
            emptyMessage="お支払い履歴はまだありません"
            caption="お支払い履歴"
            actionsHeader="領収書"
            rowActions={(row) =>
              row.amount != null && row.amount > 0 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setReceiptItem(row)}
                  className="gap-1"
                >
                  <Receipt className="w-4 h-4" />
                  発行
                </Button>
              ) : (
                <span className="text-text-sub text-sm">—</span>
              )
            }
          />

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <Button
                variant="secondary"
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                前へ
              </Button>
              <span className="text-text-sub">
                ページ {currentPage} / {totalPages}
              </span>
              <Button
                variant="secondary"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="gap-1"
              >
                次へ
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      <ReceiptDialog
        open={receiptItem !== null}
        onClose={() => setReceiptItem(null)}
        item={receiptItem}
        defaultRecipient={userName || userEmail}
      />
    </Card>
  )
}
